import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { full_name: "", role: "", department: "", nationality: "", skill_level: "Mid", project: "" };
const departments = ["All", "Contracting", "Cleaning", "Maintenance", "Airport Ground", "Operations", "HSE"];

export default function EmployeesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("All");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/employees");
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch { toast.error("Failed to load employees"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/employees", form);
      toast.success("Employee added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/employees/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((e) => {
      const matchesSearch = !q || e.full_name.toLowerCase().includes(q) || e.role.toLowerCase().includes(q) || e.project.toLowerCase().includes(q);
      const matchesDept = dept === "All" || e.department === dept;
      return matchesSearch && matchesDept;
    });
  }, [items, search, dept]);

  return (
    <div className="space-y-8" data-testid="employees-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Workforce</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">
            {total.toLocaleString()}<span className="text-primary">+</span> employees
          </h1>
          <p className="text-muted-foreground mt-2">Browse, filter and manage the operational workforce.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground" data-testid="employee-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm" data-testid="employee-add-dialog">
            <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "full_name", l: "Full name", c: 2 },
                { k: "role", l: "Role" },
                { k: "department", l: "Department" },
                { k: "nationality", l: "Nationality" },
                { k: "skill_level", l: "Skill level" },
                { k: "project", l: "Current project", c: 2 },
              ].map((f) => (
                <div key={f.k} className={f.c === 2 ? "col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">{f.l}</Label>
                  <Input
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="rounded-sm"
                    data-testid={`employee-input-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={create} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="employee-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, role, project…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm h-11 w-72 pl-9"
            data-testid="employee-search"
          />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="rounded-sm h-11 w-56" data-testid="employee-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm overflow-hidden">
        <Table data-testid="employees-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Nationality</TableHead>
              <TableHead>Skill</TableHead>
              <TableHead>Project</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">No employees match.</TableCell></TableRow>
            )}
            {filtered.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-semibold">{e.full_name}</TableCell>
                <TableCell>{e.role}</TableCell>
                <TableCell>{e.department}</TableCell>
                <TableCell className="text-muted-foreground">{e.nationality}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="rounded-sm bg-primary/10 text-primary border-primary/30 hover:bg-primary/15">
                    {e.skill_level}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{e.project}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => remove(e.id)} data-testid={`employee-delete-${e.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-muted-foreground" data-testid="employees-footnote">
        Showing {filtered.length} of {items.length} loaded employees · {total.toLocaleString()} total on payroll.
      </p>
    </div>
  );
}
